import { z } from 'zod';
import { EXPENSE_METHODS, PURCHASE_ORDER_STATUSES } from '@bulk/types';
import { dateOnlySchema, idSchema, localeSchema, pageSchema, patchOf, searchQuerySchema } from './common';

const costSchema = z.number().int().min(0).max(100_000_000).describe('Minor units of the gym currency');
const quantitySchema = z.number().int().min(1).max(100_000);
const textOrNull = (max: number) => z.string().trim().max(max).nullable().optional();
const localizedName = z.record(z.string(), z.string());

export const createSupplierSchema = z.object({
  name: z.string().trim().min(1).max(160),
  taxId: textOrNull(40),
  contactName: textOrNull(120),
  phone: textOrNull(40),
  email: z.email().max(160).nullable().optional(),
  paymentTermsDays: z.number().int().min(0).max(365).nullable().optional().describe('Days the bar has to pay after a delivery; null when not agreed'),
  notes: textOrNull(2000),
  active: z.boolean().default(true).describe('Off hides the supplier from new orders; old orders keep it'),
});
export type CreateSupplierInput = z.infer<typeof createSupplierSchema>;

export const updateSupplierSchema = patchOf(createSupplierSchema);
export type UpdateSupplierInput = z.infer<typeof updateSupplierSchema>;

export const supplierListQuerySchema = searchQuerySchema.extend({
  active: z.stringbool().optional(),
});
export type SupplierListQuery = z.infer<typeof supplierListQuerySchema>;

export const supplierItemSchema = z.object({
  itemId: idSchema,
  itemName: localizedName,
  categoryName: localizedName,
  lastUnitCostMinor: z.number().int().nullable().describe('What the last receipt from this supplier paid per unit'),
  lastCostAt: z.string().nullable(),
});
export type SupplierItemDto = z.infer<typeof supplierItemSchema>;

export const supplierSchema = z.object({
  id: idSchema,
  name: z.string(),
  taxId: z.string().nullable(),
  contactName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  paymentTermsDays: z.number().int().nullable(),
  notes: z.string().nullable(),
  active: z.boolean(),
  itemCount: z.number().int(),
  openOrders: z.number().int().describe('Orders placed or part received'),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SupplierDto = z.infer<typeof supplierSchema>;

export const supplierDetailSchema = supplierSchema.extend({ items: z.array(supplierItemSchema) });
export type SupplierDetailDto = z.infer<typeof supplierDetailSchema>;

export const setSupplierItemSchema = z.object({
  lastUnitCostMinor: costSchema.nullable().optional().describe('The price to start from when ordering; receipts update it'),
});
export type SetSupplierItemInput = z.infer<typeof setSupplierItemSchema>;

export const purchaseOrderLineInputSchema = z.object({
  itemId: idSchema,
  quantity: quantitySchema,
  unitCostMinor: costSchema.optional().describe("Left out, the supplier's last cost for the item, else 0"),
});
export type PurchaseOrderLineInput = z.infer<typeof purchaseOrderLineInputSchema>;

const orderLinesSchema = z.array(purchaseOrderLineInputSchema).min(1).max(200);

export const createPurchaseOrderSchema = z.object({
  supplierId: idSchema,
  locationId: idSchema.optional().describe('The branch the stock is for; the branch the request works at when omitted'),
  expectedOn: dateOnlySchema.nullable().optional(),
  notes: textOrNull(2000),
  lines: orderLinesSchema,
});
export type CreatePurchaseOrderInput = z.infer<typeof createPurchaseOrderSchema>;

export const updatePurchaseOrderSchema = patchOf(createPurchaseOrderSchema.pick({ expectedOn: true, notes: true, lines: true }));
export type UpdatePurchaseOrderInput = z.infer<typeof updatePurchaseOrderSchema>;

export const purchaseOrderListQuerySchema = searchQuerySchema.extend({
  status: z.enum(PURCHASE_ORDER_STATUSES).optional(),
  open: z.stringbool().optional().describe('Only orders still waiting on a delivery: placed or part received'),
  supplierId: idSchema.optional(),
  locationId: idSchema.optional(),
});
export type PurchaseOrderListQuery = z.infer<typeof purchaseOrderListQuerySchema>;

export const cancelPurchaseOrderSchema = z.object({ reason: z.string().trim().max(500).nullable().optional() });
export type CancelPurchaseOrderInput = z.infer<typeof cancelPurchaseOrderSchema>;

export const receiveLineInputSchema = z.object({
  lineId: idSchema,
  quantity: quantitySchema,
  unitCostMinor: costSchema.optional().describe('What this delivery really cost per unit when it differs from the order'),
});

export const receivePurchaseOrderSchema = z.object({
  lines: z.array(receiveLineInputSchema).min(1).max(200),
  note: textOrNull(500),
  confirmOverDelivery: z.boolean().default(false).describe('Accept more than was ordered; without it that answers 422 OVER_DELIVERY'),
});
export type ReceivePurchaseOrderInput = z.infer<typeof receivePurchaseOrderSchema>;

export const logPurchaseExpenseSchema = z.object({
  method: z.enum(EXPENSE_METHODS),
  note: textOrNull(500),
});
export type LogPurchaseExpenseInput = z.infer<typeof logPurchaseExpenseSchema>;

export const purchaseOrderLineSchema = z.object({
  id: idSchema,
  itemId: idSchema.nullable(),
  name: localizedName,
  quantity: z.number().int(),
  unitCostMinor: z.number().int(),
  receivedQuantity: z.number().int(),
  lineTotalMinor: z.number().int(),
});
export type PurchaseOrderLineDto = z.infer<typeof purchaseOrderLineSchema>;

export const purchaseOrderSchema = z.object({
  id: idSchema,
  orderNumber: z.string(),
  status: z.enum(PURCHASE_ORDER_STATUSES),
  supplierId: idSchema,
  supplierName: z.string(),
  locationId: idSchema,
  locationName: z.string(),
  expectedOn: z.string().nullable(),
  notes: z.string().nullable(),
  orderedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  closedShort: z.boolean(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  currency: z.string(),
  lineCount: z.number().int(),
  unitsOrdered: z.number().int(),
  unitsReceived: z.number().int(),
  totalMinor: z.number().int().describe('What was ordered, at the ordered costs'),
  receivedMinor: z.number().int().describe('What has arrived, at the costs on the receipts'),
  expenseId: idSchema.nullable().describe('The expense it was logged as, when one is standing'),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PurchaseOrderDto = z.infer<typeof purchaseOrderSchema>;

export const purchaseReceiptSchema = z.object({
  id: idSchema,
  note: z.string().nullable(),
  overDeliveryConfirmed: z.boolean(),
  receivedByName: z.string().nullable(),
  createdAt: z.string(),
  totalMinor: z.number().int(),
  lines: z.array(z.object({ lineId: idSchema, itemName: localizedName, quantity: z.number().int(), unitCostMinor: z.number().int() })),
});
export type PurchaseReceiptDto = z.infer<typeof purchaseReceiptSchema>;

export const purchaseOrderDetailSchema = purchaseOrderSchema.extend({
  lines: z.array(purchaseOrderLineSchema),
  receipts: z.array(purchaseReceiptSchema),
  supplierPhone: z.string().nullable(),
  supplierEmail: z.string().nullable(),
});
export type PurchaseOrderDetailDto = z.infer<typeof purchaseOrderDetailSchema>;

export const purchaseOrderPageSchema = pageSchema(purchaseOrderSchema);

export const purchaseDocumentQuerySchema = z.object({ locale: localeSchema.default('en') });
export type PurchaseDocumentQuery = z.infer<typeof purchaseDocumentQuerySchema>;

export const reorderQuerySchema = z.object({ locationId: idSchema.optional() });
export type ReorderQuery = z.infer<typeof reorderQuerySchema>;

export const reorderLevelSchema = z.object({
  itemId: idSchema,
  itemName: localizedName,
  categoryName: localizedName,
  onHand: z.number().int(),
  incoming: z.number().int().describe('Units on orders not yet received'),
  reorderLevel: z.number().int().nullable(),
  reorderQuantity: z.number().int().nullable(),
  supplierId: idSchema.nullable(),
  supplierName: z.string().nullable(),
  unitCostMinor: z.number().int().nullable(),
  suggestedQuantity: z.number().int().nullable().describe('Set when the item is at or below its level'),
});
export type ReorderLevelDto = z.infer<typeof reorderLevelSchema>;

export const reorderSuggestionsSchema = z.object({
  locationId: idSchema,
  locationName: z.string(),
  currency: z.string(),
  groups: z.array(z.object({ supplierId: idSchema.nullable(), supplierName: z.string().nullable(), lines: z.array(reorderLevelSchema) })),
  items: z.array(reorderLevelSchema).describe('Every counted item with its level, whether or not it needs ordering'),
});
export type ReorderSuggestionsDto = z.infer<typeof reorderSuggestionsSchema>;

export const setReorderLevelSchema = z.object({
  locationId: idSchema.optional(),
  reorderLevel: z.number().int().min(0).max(100_000).nullable(),
  reorderQuantity: z.number().int().min(1).max(100_000).nullable().optional(),
});
export type SetReorderLevelInput = z.infer<typeof setReorderLevelSchema>;

export const draftFromSuggestionsSchema = z.object({
  supplierId: idSchema,
  locationId: idSchema.optional(),
});
export type DraftFromSuggestionsInput = z.infer<typeof draftFromSuggestionsSchema>;
